// Gateway FAB v2 — uses @assistant-ui/react primitives.
//
// Architecture:
//   GatewayThread (this file)
//     ├─ AssistantRuntimeProvider(runtime = useLocalRuntime(adapter))
//     │  where adapter = createGatewayAdapter() bridges to the WebSocket
//     ├─ ThreadPrimitive.Root / Viewport / Messages
//     │  with UserMessage + AssistantMessage rendered through GwMessageParts
//     └─ ComposerPrimitive.Root with Input + Send / Cancel
//
// On mount it triggers chatHistory so the session opens populated rather
// than blank. Streaming + canonical history rebuild happen inside the
// adapter; this shell is presentational.

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  ThreadPrimitive,
  MessagePrimitive,
  ComposerPrimitive,
  useAuiState,
  useComposerRuntime,
} from '@assistant-ui/react';
import {
  createGatewayAdapter,
  mapGatewayContentToAui,
  stripGatewaySystemPreamble,
} from './gatewayAdapter.js';
import { GwMessageParts } from './gatewayParts.jsx';
import { GatewayClient, getGatewayClient } from './GatewayClient.js';
import { DesktopPanel } from './AssistantPanel.jsx';
import { useHubAgentState, AgentAvatar, HubAgentContext } from './shared.jsx';

const DEFAULT_SESSION_KEY = 'agent:main:main';

// Generic (non-image) attachments upload into Hub Files, which is disk-streamed
// server-side and capped at 1 GB (HUB_UPLOAD_MAX_BYTES). Enforce the same cap
// on the client so oversized files fail fast with a friendly toast instead of
// streaming a gigabyte only to be rejected.
const MAX_FILE_BYTES = 1024 * 1024 * 1024; // 1 GB
function formatFileSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

// Per-window store for images attached to the next message. The composer
// renders them as pills; the gateway adapter drains the store at send time
// and appends the markdown to the outgoing text.
const GwAttachmentsContext = React.createContext(null);

function createAttachmentStore() {
  return {
    items: [],
    listeners: new Set(),
    set(items) {
      this.items = items;
      this.listeners.forEach((listener) => { try { listener(items); } catch { /* ok */ } });
    },
    take() {
      const items = this.items;
      if (items.length) this.set([]);
      return items;
    },
  };
}

function GwUserMessage() {
  // Right-aligned orange bubble, no avatar.
  return (
    <MessagePrimitive.Root className="hub-gw2-msg is-user">
      <div className="hub-gw2-bubble">
        <GwMessageParts />
      </div>
    </MessagePrimitive.Root>
  );
}

function GwAssistantMessage() {
  // Avatar lives OUTSIDE the bubble (left), per the new design.
  return (
    <MessagePrimitive.Root className="hub-gw2-msg is-assistant">
      <HubAgentContext.Consumer>
        {(agent) => agent ? (
          <AgentAvatar agent={agent} className="hub-gw2-msg-avatar" />
        ) : (
          <span className="hub-gw2-msg-avatar hub-gw2-msg-avatar-fallback" aria-hidden="true">M</span>
        )}
      </HubAgentContext.Consumer>
      <div className="hub-gw2-bubble">
        <GwMessageParts />
      </div>
    </MessagePrimitive.Root>
  );
}

function StopIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true">
      <rect x="3" y="3" width="10" height="10" rx="2" />
    </svg>
  );
}

function PaperPlaneIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  );
}

function SmileIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </svg>
  );
}

function ImageAttachIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" />
    </svg>
  );
}

function PaperclipIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

// A plain document glyph for non-image attachments (pills + progress chip),
// standing in for a thumbnail since a generic file has no preview image.
function FileGlyphIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  );
}

// A short, curated set — deliberately not a full picker so the bundle stays
// lean. Faces, gestures, then a few objects people actually send agents.
const GW_EMOJIS = [
  '😀', '😄', '😂', '🤣', '😊', '😉', '😍', '🤩', '😎', '🥳', '😇', '🙂',
  '🤔', '🤨', '😅', '😬', '😴', '🤯', '😳', '🥺', '😢', '😭', '😤', '😮',
  '👍', '👎', '👏', '🙌', '🤝', '💪', '🙏', '✌️', '🤞', '👀', '👋', '🫡',
  '🎉', '🔥', '✨', '⭐', '❤️', '🧡', '💡', '✅', '❌', '⚡', '🚀', '☕',
];

function GwComposer() {
  const isRunning = useAuiState((state) => Boolean(state.thread.isRunning));
  const barRef = useRef(null);
  const fileRef = useRef(null);
  const fileAnyRef = useRef(null);
  const recRef = useRef(null);
  const [showEmoji, setShowEmoji] = useState(false);
  // { name, thumb (object URL), progress 0-100 } while an image uploads.
  const [pendingUpload, setPendingUpload] = useState(null);
  const [dictating, setDictating] = useState(false);
  // Drag-and-drop: true while a file is dragged over the panel, so the
  // "drop here" overlay shows. A depth counter tracks enter/leave across
  // child elements (dragleave fires when crossing into a child).
  const [dragActive, setDragActive] = useState(false);
  const dragDepth = useRef(0);
  // Uploaded-and-ready images ride as pills until send; the adapter drains
  // the store and appends their markdown to the outgoing message.
  const attachStore = useContext(GwAttachmentsContext);
  const [attachments, setAttachments] = useState(() => attachStore?.items || []);
  // Defensive: never let a missing composer context break the whole
  // composer — the hook always runs (stable order), the value may be null.
  let composerRuntime = null;
  try { composerRuntime = useComposerRuntime(); } catch { /* ok */ }
  useEffect(() => {
    if (!attachStore) return undefined;
    const listener = (items) => setAttachments(items);
    attachStore.listeners.add(listener);
    setAttachments(attachStore.items);
    return () => { attachStore.listeners.delete(listener); };
  }, [attachStore]);
  const speechSupported = typeof window !== 'undefined'
    && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

  // Insert text into the (runtime-controlled) textarea: use the native value
  // setter + an input event so React/assistant-ui pick the change up, then
  // restore focus and caret.
  const insertText = useCallback((snippet, opts) => {
    const textarea = barRef.current?.querySelector('textarea');
    if (!textarea) return;
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? textarea.value.length;
    let chunk = snippet;
    if (opts?.pad) {
      const before = textarea.value.slice(0, start);
      if (before && !/\s$/.test(before)) chunk = ` ${chunk}`;
    }
    const next = textarea.value.slice(0, start) + chunk + textarea.value.slice(end);
    const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set;
    setter.call(textarea, next);
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
    textarea.focus();
    const caret = start + chunk.length;
    try { textarea.setSelectionRange(caret, caret); } catch { /* ok */ }
  }, []);

  // Emoji popover: close on outside pointerdown or Escape.
  useEffect(() => {
    if (!showEmoji) return undefined;
    const onDown = (event) => {
      if (barRef.current && !barRef.current.contains(event.target)) setShowEmoji(false);
    };
    const onKey = (event) => { if (event.key === 'Escape') setShowEmoji(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showEmoji]);

  // Image attach: upload to Hub Gallery, then drop the image into the message
  // as markdown so the agent gets a fetchable URL (gateway chat.send has no
  // native attachment field). XHR instead of fetch purely for upload
  // progress events — the chip above the composer shows the thumbnail and a
  // real progress bar while the file is attaching. Shared by the attach
  // button and by pasting an image into the composer.
  // Returns a promise that resolves when the upload settles (success or
  // failure) so callers that attach several files at once (drag-and-drop)
  // can await them one at a time — the progress chip only shows one.
  const uploadImageFile = useCallback((file) => {
    if (!file || !/^image\//.test(file.type || '')) return Promise.resolve();
    return new Promise((resolve) => {
      const thumb = URL.createObjectURL(file);
      setPendingUpload({ name: file.name, thumb, progress: 0 });
      const finish = () => {
        URL.revokeObjectURL(thumb);
        setPendingUpload((p) => (p && p.thumb === thumb ? null : p));
      };
      const form = new FormData();
      form.append('file', file);
      form.append('folder', 'chat-uploads');
      form.append('title', file.name);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/gallery/upload');
      xhr.withCredentials = true;
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const progress = Math.min(99, Math.round((e.loaded / e.total) * 100));
        setPendingUpload((p) => (p && p.thumb === thumb ? { ...p, progress } : p));
      };
      xhr.onload = () => {
        let keepThumb = false;
        try {
          if (xhr.status < 200 || xhr.status >= 300) {
            let detail = `HTTP ${xhr.status}`;
            try { detail = JSON.parse(xhr.responseText || '{}').error || detail; } catch { /* ok */ }
            throw new Error(detail);
          }
          const entry = JSON.parse(xhr.responseText || '{}');
          if (!entry?.url) throw new Error('Upload did not return a URL');
          const absolute = new URL(entry.url, window.location.origin).href;
          // Becomes a pill, not markdown in the textarea. The thumb object URL
          // must stay alive for the pill, so skip finish() (which revokes it)
          // and only clear the progress chip. There is deliberately no
          // markdown-into-the-textarea fallback anymore.
          if (!attachStore) throw new Error('attachment store unavailable');
          attachStore.set([...attachStore.items, { name: file.name, url: absolute, thumb }]);
          keepThumb = true;
        } catch (err) {
          window.hubToast?.error?.(`Image upload failed: ${err?.message || err}`);
        } finally {
          if (keepThumb) setPendingUpload((p) => (p && p.thumb === thumb ? null : p));
          else finish();
          resolve();
        }
      };
      xhr.onerror = () => {
        window.hubToast?.error?.('Image upload failed: network error');
        finish();
        resolve();
      };
      xhr.send(form);
    });
  }, [insertText, attachStore]);

  // Generic (non-image) attach: stream the file into Hub Files, then ride it as
  // a link pill until send. Same XHR-with-progress shape as the image path, but
  // the progress chip / pill show a document glyph instead of a thumbnail and
  // the message carries a plain markdown link (not an image embed). Files land
  // in Hub Files first (folder `chat-uploads`); the returned `url` is the
  // static /hub-files/<path> the agent can fetch. Returns a promise that
  // settles when the upload finishes so drag-and-drop can await sequentially.
  const uploadGenericFile = useCallback((file) => {
    if (!file) return Promise.resolve();
    if (file.size > MAX_FILE_BYTES) {
      window.hubToast?.error?.(`"${file.name}" is larger than 1 GB and can't be attached.`);
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const token = `file-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
      setPendingUpload({ name: file.name, thumb: null, kind: 'file', mime: file.type || '', progress: 0, token });
      const finish = () => setPendingUpload((p) => (p && p.token === token ? null : p));
      const form = new FormData();
      form.append('file', file);
      form.append('folder', 'chat-uploads');
      form.append('title', file.name);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/files/upload');
      xhr.withCredentials = true;
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const progress = Math.min(99, Math.round((e.loaded / e.total) * 100));
        setPendingUpload((p) => (p && p.token === token ? { ...p, progress } : p));
      };
      xhr.onload = () => {
        try {
          if (xhr.status < 200 || xhr.status >= 300) {
            let detail = `HTTP ${xhr.status}`;
            try { detail = JSON.parse(xhr.responseText || '{}').error || detail; } catch { /* ok */ }
            throw new Error(detail);
          }
          const entry = JSON.parse(xhr.responseText || '{}');
          if (!entry?.url) throw new Error('Upload did not return a URL');
          const absolute = new URL(entry.url, window.location.origin).href;
          if (!attachStore) throw new Error('attachment store unavailable');
          attachStore.set([...attachStore.items, {
            kind: 'file',
            name: entry.filename || file.name,
            url: absolute,
            mime: entry.mime_type || file.type || '',
            size: entry.size ?? file.size,
          }]);
        } catch (err) {
          window.hubToast?.error?.(`File upload failed: ${err?.message || err}`);
        } finally {
          finish();
          resolve();
        }
      };
      xhr.onerror = () => {
        window.hubToast?.error?.('File upload failed: network error');
        finish();
        resolve();
      };
      xhr.send(form);
    });
  }, [attachStore]);

  // Route a picked/pasted/dropped file to the right uploader: images keep the
  // Gallery flow (thumbnail pill + inline image), everything else goes to Hub
  // Files (document pill + markdown link).
  const uploadAnyFile = useCallback((file) => (
    file && /^image\//.test(file.type || '')
      ? uploadImageFile(file)
      : uploadGenericFile(file)
  ), [uploadImageFile, uploadGenericFile]);

  const removeAttachment = useCallback((index) => {
    if (!attachStore) return;
    const items = attachStore.items.slice();
    const [gone] = items.splice(index, 1);
    if (gone?.thumb) { try { URL.revokeObjectURL(gone.thumb); } catch { /* ok */ } }
    attachStore.set(items);
  }, [attachStore]);

  // Send a message that has image pills. The image markdown goes INTO the
  // composer text (image first, prompt underneath) so the local echo bubble
  // renders the image immediately — not just the outgoing gateway payload.
  // The store is drained here, so the adapter's own injection is a no-op.
  const sendWithAttachments = useCallback(() => {
    try {
      const items = attachStore?.take?.() || [];
      // Images embed (![…]); generic files ride as plain markdown links so the
      // agent gets a fetchable Hub Files URL and the bubble shows a clickable
      // filename rather than a broken <img>.
      const md = items.map((a) => (
        a.kind === 'file' ? `[${a.name}](${a.url})` : `![${a.name}](${a.url})`
      )).join('\n');
      const state = composerRuntime?.getState?.();
      const current = state && typeof state.text === 'string' ? state.text.trim() : '';
      const combined = md ? (current ? `${md}\n\n${current}` : md) : current;
      if (!combined.trim()) return;
      composerRuntime?.setText?.(combined);
      composerRuntime?.send?.();
      // The image message renders a beat after send; the primitive's auto
      // scroll can miss it, so pin the viewport to the bottom across a couple
      // of frames (and once the image finishes loading and grows the bubble).
      const viewport = barRef.current?.closest?.('.hub-gw2-thread')?.querySelector?.('.hub-gw2-viewport');
      if (viewport) {
        const pin = () => { viewport.scrollTop = viewport.scrollHeight; };
        pin();
        requestAnimationFrame(pin);
        setTimeout(pin, 60);
        setTimeout(pin, 250);
      }
      // The bubble now renders from the uploaded URLs; the thumb blobs are done.
      items.forEach((a) => { if (a.thumb) { try { URL.revokeObjectURL(a.thumb); } catch { /* ok */ } } });
    } catch (err) {
      window.hubToast?.error?.(`Could not send: ${err?.message || err}`);
    }
  }, [composerRuntime, attachStore]);

  // Enter (without Shift) normally sends through the primitive, which bypasses
  // the attachment injection. When pills are present, route it through our
  // send instead. Capture-phase on the bar wrapper so the textarea's own
  // Enter handler never runs.
  const onComposerKeyDownCapture = useCallback((event) => {
    if (!attachments.length || isRunning) return;
    if (event.target?.tagName !== 'TEXTAREA') return;
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent?.isComposing) {
      event.preventDefault();
      event.stopPropagation();
      sendWithAttachments();
    }
  }, [attachments.length, isRunning, sendWithAttachments]);

  const onPickImage = useCallback((event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (file) uploadImageFile(file);
  }, [uploadImageFile]);

  // The paperclip accepts any file type; images still take the Gallery path so
  // they embed, everything else goes to Hub Files.
  const onPickFile = useCallback((event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (file) uploadAnyFile(file);
  }, [uploadAnyFile]);

  // Pasting a file (image or otherwise) starts the matching upload flow. Plain
  // text pastes fall through untouched. Only the first pasted file attaches —
  // the progress chip shows one upload at a time.
  const onPaste = useCallback((event) => {
    const items = event.clipboardData?.items;
    if (!items || !items.length) return;
    let file = null;
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.kind !== 'file') continue;
      const candidate = item.getAsFile();
      if (candidate) { file = candidate; break; }
    }
    if (!file) return;
    event.preventDefault();
    if (pendingUpload) {
      window.hubToast?.error?.('Wait for the current upload to finish.');
      return;
    }
    uploadAnyFile(file);
  }, [uploadAnyFile, pendingUpload]);

  // Drop a file (or several) onto the panel to attach it — the same flow as the
  // attach buttons / paste. Images take the Gallery path, everything else goes
  // to Hub Files; multiple files upload one after another (single progress chip).
  const onDropFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    for (const file of files) {
      // eslint-disable-next-line no-await-in-loop
      await uploadAnyFile(file);
    }
  }, [uploadAnyFile]);

  // Resolve the container the drop overlay covers: the whole FAB panel
  // when we're inside the FAB, else the thread (full-page chat). Covering
  // the panel means a drop anywhere on the FAB — header included — attaches.
  const getDropContainer = useCallback(() => (
    barRef.current?.closest('.hub-gw2-panel')
    || barRef.current?.closest('.hub-gw2-thread')
    || null
  ), []);

  // Panel-wide drag-and-drop. Listeners live on the whole FAB — not just the
  // composer — so a file dropped anywhere in it attaches. dragover must
  // preventDefault to allow the drop; we only react to drags carrying files.
  useEffect(() => {
    const panel = getDropContainer();
    if (!panel) return undefined;
    const hasFiles = (e) => {
      const types = e.dataTransfer?.types;
      return types && (Array.prototype.indexOf.call(types, 'Files') !== -1);
    };
    const onDragEnter = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setDragActive(true);
    };
    const onDragOver = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = (e) => {
      if (!hasFiles(e)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragActive(false);
    };
    const onDrop = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragActive(false);
      onDropFiles(e.dataTransfer?.files);
    };
    panel.addEventListener('dragenter', onDragEnter);
    panel.addEventListener('dragover', onDragOver);
    panel.addEventListener('dragleave', onDragLeave);
    panel.addEventListener('drop', onDrop);
    return () => {
      panel.removeEventListener('dragenter', onDragEnter);
      panel.removeEventListener('dragover', onDragOver);
      panel.removeEventListener('dragleave', onDragLeave);
      panel.removeEventListener('drop', onDrop);
    };
  }, [onDropFiles, getDropContainer]);

  const dropOverlay = dragActive ? (() => {
    const panel = getDropContainer();
    if (!panel) return null;
    return createPortal(
      <div className="hub-gw2-drop-overlay" aria-hidden="true">
        <div className="hub-gw2-drop-card">
          <span className="hub-gw2-drop-ic">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M17 8l-5-5-5 5" />
              <path d="M12 3v12" />
            </svg>
          </span>
          <span className="hub-gw2-drop-title">Drop files to attach</span>
        </div>
      </div>,
      panel,
    );
  })() : null;

  // Mic = browser dictation (Web Speech API); final transcripts append into
  // the input. The button never renders on unsupported browsers.
  const toggleDictation = useCallback(() => {
    if (recRef.current) {
      try { recRef.current.stop(); } catch { /* ok */ }
      recRef.current = null;
      setDictating(false);
      return;
    }
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Ctor) return;
    const rec = new Ctor();
    rec.lang = navigator.language || 'en-US';
    rec.interimResults = false;
    rec.continuous = true;
    rec.onresult = (event) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        if (event.results[i].isFinal) transcript += event.results[i][0].transcript;
      }
      if (transcript.trim()) insertText(transcript.trim(), { pad: true });
    };
    rec.onend = () => { recRef.current = null; setDictating(false); };
    rec.onerror = () => { recRef.current = null; setDictating(false); };
    try {
      rec.start();
      recRef.current = rec;
      setDictating(true);
    } catch {
      recRef.current = null;
      setDictating(false);
    }
  }, [insertText]);

  useEffect(() => () => { try { recRef.current?.stop(); } catch { /* ok */ } }, []);

  return (
    <div className="hub-gw2-composer-bar" ref={barRef} onPaste={onPaste} onKeyDownCapture={onComposerKeyDownCapture}>
      {pendingUpload ? (
        <div className="hub-gw2-upload-chip">
          {pendingUpload.thumb ? (
            <img className="hub-gw2-upload-thumb" src={pendingUpload.thumb} alt="" />
          ) : (
            <span className="hub-gw2-upload-thumb hub-gw2-upload-fileic" aria-hidden="true">
              <FileGlyphIcon />
            </span>
          )}
          <div className="hub-gw2-upload-meta">
            <span className="hub-gw2-upload-name">{pendingUpload.name}</span>
            <span className="hub-gw2-upload-track">
              <span className="hub-gw2-upload-fill" style={{ width: `${pendingUpload.progress}%` }} />
            </span>
          </div>
          <span className="hub-gw2-upload-pct">{pendingUpload.progress}%</span>
        </div>
      ) : null}
      {attachments.length ? (
        <div className="hub-gw2-attach-row">
          {attachments.map((attachment, index) => (
            <span
              className={`hub-gw2-attach-pill${attachment.kind === 'file' ? ' is-file' : ''}`}
              key={`${attachment.url}-${index}`}
              title={attachment.kind === 'file' && attachment.size ? `${attachment.name} · ${formatFileSize(attachment.size)}` : attachment.name}
            >
              {attachment.kind === 'file' ? (
                <span className="hub-gw2-attach-thumb hub-gw2-attach-fileic" aria-hidden="true">
                  <FileGlyphIcon />
                </span>
              ) : (
                <img className="hub-gw2-attach-thumb" src={attachment.thumb || attachment.url} alt="" />
              )}
              <span className="hub-gw2-attach-name">{attachment.name}</span>
              {attachment.kind === 'file' && formatFileSize(attachment.size) ? (
                <span className="hub-gw2-attach-size">{formatFileSize(attachment.size)}</span>
              ) : null}
              <button
                type="button"
                className="hub-gw2-attach-remove"
                aria-label={`Remove ${attachment.name}`}
                title="Remove"
                onClick={() => removeAttachment(index)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <ComposerPrimitive.Root className="hub-gw2-composer">
        <button
          type="button"
          className={`hub-gw2-composer-tool${showEmoji ? ' is-active' : ''}`}
          aria-label="Insert emoji"
          title="Insert emoji"
          onClick={() => setShowEmoji((v) => !v)}
        >
          <SmileIcon />
        </button>
        <button
          type="button"
          className="hub-gw2-composer-tool"
          aria-label="Attach an image"
          title="Attach an image"
          disabled={Boolean(pendingUpload)}
          onClick={() => fileRef.current?.click()}
        >
          <ImageAttachIcon />
        </button>
        <button
          type="button"
          className="hub-gw2-composer-tool"
          aria-label="Attach a file"
          title="Attach a file (up to 1 GB)"
          disabled={Boolean(pendingUpload)}
          onClick={() => fileAnyRef.current?.click()}
        >
          <PaperclipIcon />
        </button>
        <ComposerPrimitive.Input
          className="hub-gw2-composer-input"
          placeholder={isRunning ? 'Agent is responding…' : 'Send a message…'}
          rows={1}
          autoFocus
        />
        {speechSupported ? (
          <button
            type="button"
            className={`hub-gw2-composer-tool${dictating ? ' is-recording' : ''}`}
            aria-label={dictating ? 'Stop dictation' : 'Dictate a message'}
            title={dictating ? 'Stop dictation' : 'Dictate a message'}
            onClick={toggleDictation}
          >
            <MicIcon />
          </button>
        ) : null}
        {isRunning ? (
          <ComposerPrimitive.Cancel
            className="hub-gw2-composer-cancel"
            aria-label="Stop response"
            title="Stop response"
          >
            <StopIcon />
          </ComposerPrimitive.Cancel>
        ) : attachments.length ? (
          <button
            type="button"
            className="hub-gw2-composer-send"
            aria-label="Send"
            title="Send"
            onClick={sendWithAttachments}
          >
            <PaperPlaneIcon />
          </button>
        ) : (
          <ComposerPrimitive.Send
            className="hub-gw2-composer-send"
            aria-label="Send"
            title="Send"
          >
            <PaperPlaneIcon />
          </ComposerPrimitive.Send>
        )}
      </ComposerPrimitive.Root>
      {showEmoji ? (
        <div className="hub-gw2-emoji-pop" role="menu" onPointerDown={(e) => e.preventDefault()}>
          {GW_EMOJIS.map((emoji) => (
            <button
              type="button"
              key={emoji}
              onClick={() => { insertText(emoji); setShowEmoji(false); }}
            >
              {emoji}
            </button>
          ))}
        </div>
      ) : null}
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickImage} />
      <input ref={fileAnyRef} type="file" hidden onChange={onPickFile} />
      {dropOverlay}
    </div>
  );
}

// Full-screen preview for an image inside a chat bubble. Imperative DOM on
// purpose: it outlives any one React window, and Esc / backdrop / × all
// close it.
function openGwLightbox(src, alt) {
  if (!src) return;
  const existing = document.getElementById('hub-gw2-lightbox');
  if (existing) existing.remove();
  const overlay = document.createElement('div');
  overlay.id = 'hub-gw2-lightbox';
  overlay.className = 'hub-gw2-lightbox';
  const img = document.createElement('img');
  img.src = src;
  img.alt = alt || '';
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'hub-gw2-lightbox-close';
  close.setAttribute('aria-label', 'Close preview');
  close.textContent = '×';
  overlay.append(img, close);
  const onKey = (event) => { if (event.key === 'Escape') remove(); };
  const remove = () => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  };
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay || event.target === close) remove();
  });
  document.addEventListener('keydown', onKey);
  document.body.appendChild(overlay);
}

// Intelligent auto-scroll: stay glued to the bottom while the user is there,
// release when they scroll up, re-engage when they return, and surface a
// "NEW (n)" pill when fresh messages arrive while they're scrolled up. Owns
// scrolling entirely (Viewport autoScroll is off) so nothing fights it.
function GwScrollDock() {
  const rootRef = useRef(null);
  const stateRef = useRef({ pinned: true, lastCount: 0, viewport: null, raf: 0, far: false });
  const [pinned, setPinned] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const [far, setFar] = useState(false);
  const THRESHOLD = 56;
  // Past this distance above the live edge, a quiet "back to live" button
  // appears even when nothing new has arrived.
  const FAR_THRESHOLD = 240;

  useEffect(() => {
    const viewport = rootRef.current?.closest?.('.hub-gw2-thread')?.querySelector?.('.hub-gw2-viewport');
    if (!viewport) return undefined;
    const st = stateRef.current;
    st.viewport = viewport;
    const countMsgs = () => viewport.querySelectorAll('.hub-gw2-msg').length;
    const distance = () => viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
    const atBottom = () => distance() <= THRESHOLD;
    const toBottom = (smooth) => {
      try { viewport.scrollTo({ top: viewport.scrollHeight, behavior: smooth ? 'smooth' : 'auto' }); }
      catch { viewport.scrollTop = viewport.scrollHeight; }
    };
    const syncFar = () => {
      const isFar = distance() > FAR_THRESHOLD;
      if (isFar !== st.far) { st.far = isFar; setFar(isFar); }
    };

    st.lastCount = countMsgs();
    toBottom(false);

    const onScroll = () => {
      const bottom = atBottom();
      if (bottom !== st.pinned) { st.pinned = bottom; setPinned(bottom); }
      if (bottom) { st.lastCount = countMsgs(); setNewCount(0); }
      syncFar();
    };
    viewport.addEventListener('scroll', onScroll, { passive: true });

    // Coalesce the mutation storm from token streaming into one check per frame.
    const onMutate = () => {
      if (st.raf) return;
      st.raf = requestAnimationFrame(() => {
        st.raf = 0;
        const count = countMsgs();
        if (st.pinned) { st.lastCount = count; toBottom(false); return; }
        if (count > st.lastCount) {
          setNewCount((n) => n + (count - st.lastCount));
          st.lastCount = count;
        }
        // Streaming growth can push the user past the threshold without a
        // scroll event, so re-check here too.
        syncFar();
      });
    };
    const mo = new MutationObserver(onMutate);
    mo.observe(viewport, { childList: true, subtree: true, characterData: true });

    return () => {
      viewport.removeEventListener('scroll', onScroll);
      mo.disconnect();
      if (st.raf) cancelAnimationFrame(st.raf);
    };
  }, []);

  const jump = useCallback(() => {
    const vp = stateRef.current.viewport;
    if (!vp) return;
    try { vp.scrollTo({ top: vp.scrollHeight, behavior: 'smooth' }); } catch { vp.scrollTop = vp.scrollHeight; }
    stateRef.current.pinned = true;
    stateRef.current.lastCount = vp.querySelectorAll('.hub-gw2-msg').length;
    stateRef.current.far = false;
    setPinned(true);
    setNewCount(0);
    setFar(false);
  }, []);

  const arrow = (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
  return (
    <div ref={rootRef} className="hub-gw2-scrolldock">
      {(!pinned && newCount > 0) ? (
        <button type="button" className="hub-gw2-newpill" onClick={jump} aria-label={`${newCount} new message${newCount === 1 ? '' : 's'}, jump to latest`}>
          <span className="hub-gw2-newpill-txt">NEW ({newCount})</span>
          {arrow}
        </button>
      ) : (!pinned && far) ? (
        <button type="button" className="hub-gw2-newpill is-jump" onClick={jump} aria-label="Scroll back to the latest messages">
          {arrow}
          <span className="hub-gw2-newpill-txt">Latest</span>
        </button>
      ) : null}
    </div>
  );
}

function GwThreadShell() {
  // Delegated: any image inside a bubble opens the lightbox. display:contents
  // keeps the wrapper out of the flex layout.
  const onThreadClick = useCallback((event) => {
    const img = event.target?.closest?.('.hub-gw2-bubble img');
    if (img?.src) {
      event.preventDefault();
      openGwLightbox(img.src, img.alt);
    }
  }, []);
  return (
    <div style={{ display: 'contents' }} onClick={onThreadClick}>
    <ThreadPrimitive.Root className="hub-gw2-thread">
      <ThreadPrimitive.Viewport className="hub-gw2-viewport" autoScroll={false}>
        <ThreadPrimitive.Empty>
          <div className="hub-gw2-empty">
            <span className="hub-gw2-empty-ic" aria-hidden="true">
              <HubAgentContext.Consumer>
                {(agent) => <AgentAvatar agent={agent} className="hub-gw2-empty-av" />}
              </HubAgentContext.Consumer>
            </span>
            <p>Send a message to get started.</p>
          </div>
        </ThreadPrimitive.Empty>
        <ThreadPrimitive.Messages
          components={{ UserMessage: GwUserMessage, AssistantMessage: GwAssistantMessage }}
        />
      </ThreadPrimitive.Viewport>
      <GwScrollDock />
      <GwComposer />
    </ThreadPrimitive.Root>
    </div>
  );
}

// Pre-load chat.history into the AUI thread on mount so opening the FAB
// shows the existing transcript instead of starting blank. The local
// runtime exposes thread.reset(messages) and accepts ThreadMessageLike,
// whose content can be a typed array of {text, reasoning, tool-call,
// ...} parts — so we feed historical exec blocks through the same
// mapping the live adapter uses (mapGatewayContentToAui) and they
// render through GwToolCallPart instead of being flattened to text.
// `sessionKey` may be `null` to mean "use whatever the gateway hands us
// after the connect handshake" — we re-resolve at fetch time.
// Auto-send prompts keyed by session key. window.hubAssistant.openChatWithPrompt
// stores the prompt here before its window mounts; useGatewayBootstrap consumes
// it once history has loaded and appends it as the first user turn (which
// kicks off a normal adapter run, so it streams like a hand-typed message).
const pendingPrompts = new Map();

function flushPendingPrompt(runtime, key) {
  if (!key || !pendingPrompts.has(key)) return;
  const prompt = pendingPrompts.get(key);
  pendingPrompts.delete(key);
  if (!prompt) return;
  try {
    runtime?.thread?.append?.({ role: 'user', content: [{ type: 'text', text: prompt }] });
  } catch (err) {
    console.warn('[gw-v2] pending prompt append failed', err);
  }
}

function useGatewayBootstrap(runtime, sessionKey, onResolvedKey, clientOverride = null) {
  useEffect(() => {
    let cancelled = false;
    const client = clientOverride || getGatewayClient();
    client.connect();

    async function load() {
      try {
        if (client.state !== 'ready') {
          await new Promise((resolve) => {
            const onState = (e) => {
              if (e.detail?.state === 'ready') {
                client.removeEventListener('state', onState);
                resolve();
              }
            };
            client.addEventListener('state', onState);
            setTimeout(() => {
              client.removeEventListener('state', onState);
              resolve();
            }, 8000);
          });
        }
        if (cancelled) return;
        const targetKey = sessionKey || client.sessionKey || DEFAULT_SESSION_KEY;
        onResolvedKey?.(targetKey);
        const payload = await client.chatHistory({ sessionKey: targetKey, limit: 50 });
        if (cancelled) return;
        const msgs = Array.isArray(payload?.messages) ? payload.messages : [];
        if (!msgs.length) { flushPendingPrompt(runtime, targetKey); return; }

        // Past assistant turns split toolCall + toolResult across separate
        // history messages:
        //   msg N:   role=assistant content=[{type:'toolCall', id:'call_x', name, arguments}]
        //   msg N+1: role=toolResult toolCallId='call_x' content=[{type:'text', text:'…'}]
        // Naively running each message through mapGatewayContentToAui
        // emits the toolCall part with NO result (the result lived on a
        // separate message we'd filter out as not-assistant). Without a
        // result, GwToolCallPart's `running = result === undefined` keeps
        // every past tool card stuck on a 'running' chip. Build a global
        // toolCallId → result index across the whole history, then merge
        // results back into each toolCall when mapping.
        const resultByCallId = new Map();
        for (const m of msgs) {
          if (!m) continue;
          if (m.role === 'toolResult' || m.role === 'tool_result') {
            const id = m.toolCallId || m.tool_use_id || m.tool_call_id || null;
            if (id) {
              resultByCallId.set(id, {
                content: m.content,
                isError: !!m.isError,
              });
            }
          }
          if (Array.isArray(m.content)) {
            for (const c of m.content) {
              if (!c || typeof c !== 'object') continue;
              if (c.type === 'toolResult' || c.type === 'tool_result') {
                const id = c.tool_use_id || c.callId || c.id || null;
                if (id) {
                  resultByCallId.set(id, {
                    content: c.content ?? c.output ?? c.result,
                    isError: !!c.isError,
                  });
                }
              }
            }
          }
        }
        const flattenResultContent = (content) => {
          if (Array.isArray(content)) {
            return content
              .filter((cc) => cc && (cc.type === 'text' || typeof cc.text === 'string'))
              .map((cc) => (typeof cc.text === 'string' ? cc.text : ''))
              .join('');
          }
          if (typeof content === 'string') return content;
          if (content && typeof content === 'object') {
            try { return JSON.stringify(content); } catch { return String(content); }
          }
          return '';
        };

        const typed = msgs
          .filter((m) => m?.role === 'user' || m?.role === 'assistant')
          .map((m) => {
            if (m.role === 'user') {
              // Strip the gateway-injected "System: [time] Exec
              // completed… [time] <real text>" preamble so the user
              // bubble only shows what the operator actually typed.
              const raw = Array.isArray(m.content)
                ? m.content.filter((c) => c?.type === 'text').map((c) => c.text || '').join('')
                : (typeof m.content === 'string' ? m.content : String(m.text || ''));
              const cleaned = stripGatewaySystemPreamble(raw).trim();
              if (!cleaned) return null;
              return { role: 'user', content: [{ type: 'text', text: cleaned }] };
            }
            // Walk the assistant content[] inline so we can pair toolCall
            // blocks against the global resultByCallId map. We deliberately
            // do NOT reuse mapGatewayContentToAui here because that helper
            // only sees one message's content at a time.
            const parts = [];
            if (Array.isArray(m.content)) {
              for (const c of m.content) {
                if (!c || typeof c !== 'object') continue;
                if (c.type === 'text' && typeof c.text === 'string' && c.text) {
                  parts.push({ type: 'text', text: c.text });
                } else if (c.type === 'thinking' || c.type === 'reasoning') {
                  const text = (typeof c.thinking === 'string' && c.thinking) || (typeof c.text === 'string' && c.text) || '';
                  if (text.trim()) parts.push({ type: 'reasoning', text });
                } else if (c.type === 'toolCall' || c.type === 'tool_call') {
                  const callId = c.id || c.callId || c.toolCallId || `tc-h-${parts.length}`;
                  const part = {
                    type: 'tool-call',
                    toolCallId: String(callId),
                    toolName: c.name || c.toolName || 'tool',
                    args: c.arguments ?? c.args ?? {},
                  };
                  const matched = resultByCallId.get(callId);
                  if (matched) {
                    part.result = flattenResultContent(matched.content);
                    part.isError = !!matched.isError;
                  }
                  parts.push(part);
                }
              }
            }
            if (parts.length) return { role: 'assistant', content: parts };
            const fallback = String(m.text || '').trim();
            if (!fallback) return null;
            return { role: 'assistant', content: [{ type: 'text', text: fallback }] };
          })
          .filter(Boolean);

        try {
          // useLocalRuntime's thread.reset accepts ThreadMessageLike[], so
          // typed tool-call / reasoning parts render through the same
          // GwMessageParts components that handle the live stream.
          runtime?.thread?.reset?.(typed);
        } catch (err) {
          console.warn('[gw-v2] thread.reset failed', err);
        }
        flushPendingPrompt(runtime, targetKey);
      } catch (err) {
        console.warn('[gw-v2] bootstrap failed', err);
        // A brand-new conversation has no gateway session yet, so chatHistory
        // may reject — the pending first prompt must still go out.
        if (!cancelled) flushPendingPrompt(runtime, sessionKey || client.sessionKey || DEFAULT_SESSION_KEY);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [runtime, sessionKey, onResolvedKey, clientOverride]);
}

// Lives inside the AssistantRuntimeProvider so it can read the thread's
// messages. On every change it flattens them to {role, content:text} and
// hands them up — the window decides whether to (re)generate a title.
// Session keys that have already been auto-titled this page load — so we name a
// chat ONCE (not on every message change / remount, which burns tokens).
const autoTitledKeys = new Set();

function AutoTitleWatcher({ onMessages }) {
  const messages = useAuiState((s) => s.thread.messages);
  useEffect(() => {
    if (typeof onMessages !== 'function') return;
    const flat = (messages || []).map((m) => {
      const text = Array.isArray(m.content)
        ? m.content.filter((p) => p && p.type === 'text').map((p) => p.text).join(' ').trim()
        : String(m.content || '').trim();
      return { role: m.role, content: text };
    }).filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content);
    onMessages(flat);
  }, [messages, onMessages]);
  return null;
}

export function GatewayThread({ sessionKey: explicitKey = null, onResolvedKey, agent = null, client = null, onMessages = null } = {}) {
  // We deliberately resolve sessionKey lazily inside the adapter (it reads
  // client.sessionKey on every run), so the FAB picks up the gateway's
  // chosen session key after the WS handshake completes — not the
  // hard-coded DEFAULT_SESSION_KEY captured at first React render.
  // `client` is the per-window GatewayClient (own WebSocket); when omitted
  // everything rides the shared singleton.
  const attachStore = useMemo(() => createAttachmentStore(), []);
  const adapter = useMemo(
    () => createGatewayAdapter({
      client,
      getSessionKey: () => explicitKey || (client || getGatewayClient()).sessionKey || DEFAULT_SESSION_KEY,
      takeAttachmentsMarkdown: () => attachStore.take().map((a) => `![${a.name}](${a.url})`).join('\n'),
    }),
    [explicitKey, client, attachStore],
  );
  const runtime = useLocalRuntime(adapter);
  useGatewayBootstrap(runtime, explicitKey, onResolvedKey, client);

  return (
    <HubAgentContext.Provider value={agent}>
      <GwAttachmentsContext.Provider value={attachStore}>
        <AssistantRuntimeProvider runtime={runtime}>
          {onMessages ? <AutoTitleWatcher onMessages={onMessages} /> : null}
          <GwThreadShell />
        </AssistantRuntimeProvider>
      </GwAttachmentsContext.Provider>
    </HubAgentContext.Provider>
  );
}

// ── Status pill ──────────────────────────────────────────────────────────
function statusMeta(state) {
  switch (state) {
    case 'ready':       return { color: 'green',  label: 'connected' };
    case 'handshaking': return { color: 'yellow', label: 'handshaking' };
    case 'connecting':  return { color: 'yellow', label: 'connecting' };
    case 'closed':      return { color: 'red',    label: 'closed' };
    case 'error':       return { color: 'red',    label: 'error' };
    case 'stopped':     return { color: 'red',    label: 'stopped' };
    default:            return { color: 'gray',   label: 'idle' };
  }
}

function useGatewayState(clientOverride = null) {
  const client = useMemo(() => clientOverride || getGatewayClient(), [clientOverride]);
  const [state, setState] = useState({ state: client.state || 'idle' });
  useEffect(() => {
    const onState = (e) => setState({ state: e.detail.state, detail: e.detail });
    client.addEventListener('state', onState);
    return () => client.removeEventListener('state', onState);
  }, [client]);
  return state;
}

function shortSessionLabel(key) {
  if (!key) return '(no session)';
  return String(key).replace(/^agent:main:/, '');
}

// ── Header sub-components (new design) ───────────────────────────────────

// Single "New Chat" pill — clears the current chat by calling the
// reset flow the operator already wired up (sessions.reset + thread
// remount). No dropdown, no session switcher.
function StartOverButton({ onReset, isReady }) {
  return (
    <button
      type="button"
      className="hub-gw2-startover-btn"
      onClick={onReset}
      disabled={!isReady}
      title="Clear this chat (starts a fresh conversation)"
    >
      <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M2 4.5h9.5a3 3 0 0 1 0 6H8" />
        <path d="M4.5 2L2 4.5 4.5 7" />
      </svg>
      <span>Clear Chat</span>
    </button>
  );
}

// The Show Desktop control lives in the sidebar footer (above the agent
// launcher) — shared across all chat windows instead of repeated in every
// window header. ⌘/Ctrl+U still toggles it globally.

// ── Panel chrome (launcher + multi-window manager) ───────────────────────
//
// Up to MAX_OPEN_WINDOWS chat windows float at once, each bound to its own
// hub-tracked conversation (session key). Opening more auto-minimizes the
// least-recently-focused window into a LinkedIn-style dock pinned along the
// bottom of the screen. The window set (keys, titles, open/minimized,
// dragged rects/sizes) persists in localStorage so reloads and SPA
// navigation restore the same layout. All windows share the one gateway
// WebSocket — the adapter filters frames by sessionKey per thread.
const WINDOWS_KEY = 'hub-gw2-windows-v1';
const LEGACY_OPEN_KEY = 'hub-gw2-panel-open';
const LEGACY_RECT_KEY = 'hub-gw2-panel-rect';
const LEGACY_SIZE_KEY = 'hub-gw2-panel-size';
const MAX_OPEN_WINDOWS = 3;
// Hard cap on total windows — each window owns its own WebSocket, so this
// also bounds gateway connections per tab.
const MAX_TOTAL_WINDOWS = 4;
// Sit above the floating VNC desktop dialog (z-index: 9997, 10090 when
// expanded), same as the single-panel era.
const PANEL_BASE_Z = 10100;
const PANEL_MIN_WIDTH = 320;
const PANEL_MIN_HEIGHT = 360;
const PANEL_DEFAULT_WIDTH = 420;
const PANEL_STACK_GAP = 16;

function sanitizeRect(rect) {
  return (rect && typeof rect === 'object' && typeof rect.left === 'number' && typeof rect.top === 'number')
    ? { left: rect.left, top: rect.top }
    : null;
}

function sanitizeSize(size) {
  return (size && typeof size === 'object' && typeof size.width === 'number' && typeof size.height === 'number')
    ? { width: size.width, height: size.height }
    : null;
}

// window.key === null means "the user's default conversation" — it gets its
// concrete hub key once the gateway handshake resolves. At most one null-key
// window can exist.
function windowId(w) { return w.key === null ? '__default__' : w.key; }

function loadWindows() {
  try {
    const raw = window.localStorage.getItem(WINDOWS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.windows)) {
        const seen = new Set();
        const out = [];
        for (const w of parsed.windows) {
          if (!w || (w.key !== null && typeof w.key !== 'string')) continue;
          const id = w.key === null ? '__default__' : w.key;
          if (seen.has(id)) continue;
          seen.add(id);
          out.push({
            key: w.key,
            title: typeof w.title === 'string' ? w.title : '',
            mode: w.mode === 'min' ? 'min' : 'open',
            // Re-fit the saved position to the CURRENT viewport so a rect
            // that ended up mostly offscreen (or was saved on a bigger
            // window) snaps back fully on screen on restore.
            rect: clampRectToViewport(sanitizeRect(w.rect), sanitizeSize(w.size)),
            size: sanitizeSize(w.size),
            lastFocusAt: typeof w.lastFocusAt === 'number' ? w.lastFocusAt : 0,
          });
        }
        return out;
      }
    }
    // Migrate the single-panel era: the open flag plus its saved rect/size
    // become one window on the default conversation.
    if (window.localStorage.getItem(LEGACY_OPEN_KEY) === '1') {
      let rect = null;
      let size = null;
      try { rect = sanitizeRect(JSON.parse(window.localStorage.getItem(LEGACY_RECT_KEY) || 'null')); } catch {}
      try { size = sanitizeSize(JSON.parse(window.localStorage.getItem(LEGACY_SIZE_KEY) || 'null')); } catch {}
      return [{ key: null, title: '', mode: 'open', rect, size, lastFocusAt: 1 }];
    }
  } catch {}
  return [];
}

function saveWindows(windows) {
  try {
    window.localStorage.setItem(WINDOWS_KEY, JSON.stringify({
      windows: windows.map(({ key, title, mode, rect, size, lastFocusAt }) => (
        { key, title, mode, rect, size, lastFocusAt }
      )),
    }));
    // One-way migration — drop the legacy flag so old and new state never
    // disagree about what should be open.
    window.localStorage.removeItem(LEGACY_OPEN_KEY);
  } catch {}
}

// Minimize least-recently-focused open windows until at most
// MAX_OPEN_WINDOWS float. Opening a 4th conversation never blocks — the
// oldest window drops into the dock instead (LinkedIn chat behavior).
function enforceOpenCap(windows) {
  const open = windows.filter((w) => w.mode === 'open');
  if (open.length <= MAX_OPEN_WINDOWS) return windows;
  const overflow = open
    .slice()
    .sort((a, b) => (a.lastFocusAt || 0) - (b.lastFocusAt || 0))
    .slice(0, open.length - MAX_OPEN_WINDOWS);
  const minimize = new Set(overflow.map(windowId));
  return windows.map((w) => (minimize.has(windowId(w)) ? { ...w, mode: 'min' } : w));
}

// Keep the WHOLE panel on screen — not just a sliver. The panel is
// top-anchored once dragged/resized, so a saved `top` near the bottom (e.g.
// an accidental low drag) would push the body offscreen, leaving only the
// header visible. Clamping against the panel's own height (falling back to
// the CSS default when unresized) guarantees header + messages + composer
// all fit. Applied on drag, resize AND load, so a bad saved rect self-heals
// after logout/login instead of restoring mostly offscreen.
function clampRectToViewport(rect, size) {
  if (!rect) return rect;
  const margin = 8;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const w = (size && size.width) || Math.min(PANEL_DEFAULT_WIDTH, vw - 32);
  const h = (size && size.height) || Math.min(560, vh - 140);
  return {
    left: Math.min(Math.max(margin, rect.left), Math.max(margin, vw - margin - w)),
    top: Math.min(Math.max(margin, rect.top), Math.max(margin, vh - margin - h)),
  };
}

// One floating chat window: draggable header, bottom-right resize, visible
// minimize + close controls, and its own GatewayThread bound to win.key.
// Minimized windows stay mounted (display:none) so streams and scroll
// position survive the round-trip through the dock.
function ChatWindow({
  win, agent, zIndex, stackIndex, anchorLeft,
  onFocus, onMinimize, onClose, onLayout, onConversationChange, onClientState,
}) {
  // Every window owns its own WebSocket to the gateway proxy (the thread's
  // bootstrap calls connect(); close() on unmount tears it down). Only the
  // default (null-key) window's client adopts the user's default hub
  // conversation on connect — explicit-key windows are dedicated pipes.
  const client = useMemo(
    () => new GatewayClient({ adoptDefaultConversation: win.key === null }),
    [], // one client per mounted window — the key may change identity later
  );
  useEffect(() => () => client.close(), [client]);
  const state = useGatewayState(client);
  const isReady = state.state === 'ready';
  const winId = windowId(win);
  useEffect(() => { onClientState?.(winId, state.state); }, [onClientState, winId, state.state]);

  const [rect, setRect] = useState(win.rect);
  const [size, setSize] = useState(win.size);
  const rectRef = useRef(rect);
  useEffect(() => { rectRef.current = rect; }, [rect]);
  const sizeRef = useRef(size);
  useEffect(() => { sizeRef.current = size; }, [size]);

  const persistLayout = useCallback(() => {
    onLayout(win.key, rectRef.current, sizeRef.current);
  }, [onLayout, win.key]);

  const beginPanelResize = useCallback((event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    // Each handle declares which corner it lives on via data-corner. The
    // corner determines which edges grow/shrink as the pointer moves and
    // which opposite edge stays pinned in place.
    const corner = event.currentTarget.dataset.corner || 'tl';
    const growsRight = corner === 'tr' || corner === 'br';
    const growsDown = corner === 'bl' || corner === 'br';
    const panelEl = event.currentTarget.closest('.hub-gateway-panel');
    if (!panelEl) return;
    const startRect = panelEl.getBoundingClientRect();
    const start = {
      x: event.clientX,
      y: event.clientY,
      width: startRect.width,
      height: startRect.height,
      left: startRect.left,
      top: startRect.top,
    };
    const onMove = (moveEvent) => {
      const dx = moveEvent.clientX - start.x;
      const dy = moveEvent.clientY - start.y;
      const nextWidth = Math.max(
        PANEL_MIN_WIDTH,
        growsRight ? start.width + dx : start.width - dx,
      );
      const nextHeight = Math.max(
        PANEL_MIN_HEIGHT,
        growsDown ? start.height + dy : start.height - dy,
      );
      // Pin the corner opposite the dragged handle.
      const nextLeft = growsRight ? start.left : start.left + (start.width - nextWidth);
      const nextTop = growsDown ? start.top : start.top + (start.height - nextHeight);
      setSize({ width: nextWidth, height: nextHeight });
      setRect(clampRectToViewport({ left: nextLeft, top: nextTop }, { width: nextWidth, height: nextHeight }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      persistLayout();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
  }, [persistLayout]);

  const beginPanelDrag = useCallback((event) => {
    if (event.button !== 0) return;
    // Don't start a drag when the user clicks an interactive control inside
    // the header (minimize/close, toolbar buttons, etc).
    if (event.target?.closest?.('button, a, input, textarea, select')) return;
    event.preventDefault();
    const panelEl = event.currentTarget.closest('.hub-gateway-panel');
    if (!panelEl) return;
    const startRect = panelEl.getBoundingClientRect();
    const start = {
      x: event.clientX,
      y: event.clientY,
      left: startRect.left,
      top: startRect.top,
    };
    const onMove = (moveEvent) => {
      setRect(clampRectToViewport({
        left: start.left + (moveEvent.clientX - start.x),
        top: start.top + (moveEvent.clientY - start.y),
      }, sizeRef.current));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      persistLayout();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
  }, [persistLayout]);

  const resetSession = async () => {
    if (!isReady) return;
    // Allocate a durable hub-tracked conversation for THIS window. The old
    // conversation stays on record — both survive a reload.
    try {
      const conv = await client.newConversation();
      onConversationChange(win.key, conv);
      try { window.hubToast?.success?.('Chat cleared'); } catch {}
    } catch (err) {
      try { window.hubToast?.error?.(`New chat failed: ${err.message || err}`); } catch {}
    }
  };

  // Auto-title state: 'idle' → 'pending' (shimmering while the agent names
  // the chat) → 'named' (brief reveal). Refs gate how often we ask and stop
  // the moment the user renames it themselves.
  const [titleStatus, setTitleStatus] = useState('idle');
  const autoTitleCountRef = useRef(0);
  const autoTitleBusyRef = useRef(false);
  const userRenamedRef = useRef(false);

  // In-place rename: click the title in the secondary header row, type,
  // Enter/blur commits (PATCH by session key), Escape cancels. Max 30 chars.
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const commitTitle = useCallback(async () => {
    const next = titleDraft.trim().slice(0, 30);
    setEditingTitle(false);
    if (!win.key || !next || next === win.title) return;
    // A human named it — never let auto-titling touch it again.
    userRenamedRef.current = true;
    setTitleStatus('idle');
    try {
      const r = await fetch(`/api/openclaw/chat-sessions/${encodeURIComponent(win.key)}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: next }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      onConversationChange(win.key, { key: win.key, title: next });
    } catch (err) {
      try { window.hubToast?.error?.(`Rename failed: ${err.message || err}`); } catch {}
    }
  }, [titleDraft, win.key, win.title, onConversationChange]);

  // MUST be identity-stable: it sits in the thread bootstrap effect's dep
  // list. An inline arrow here re-ran the effect every render, and each run
  // reset the client's retry budget — a failed gateway turned that into an
  // unbounded connect/config-fetch loop.
  const handleResolvedKey = useCallback((k) => {
    if (win.key !== null || !k) return;
    // Never persist the shared main session as the default window's
    // identity — that's only the bootstrap's transient fallback when the
    // hub-conversation adoption hasn't completed (offline / 8s timeout).
    // Leaving the window null-keyed lets the next connect resolve it to a
    // real hub conversation instead of permanently landing on main.
    if (k === DEFAULT_SESSION_KEY || k === client.mainSessionKey) return;
    onConversationChange(null, { key: k, title: client.conversation?.title || win.title });
  }, [win.key, win.title, onConversationChange, client]);

  // Ask the backend to name this chat from its transcript, shimmering the
  // header title until the words come back, then revealing them.
  const runAutoTitle = useCallback(async (messages) => {
    const key = win.key;
    if (!key) return;
    autoTitleBusyRef.current = true;
    setTitleStatus('pending');
    try {
      const r = await fetch(`/api/openclaw/chat-sessions/${encodeURIComponent(key)}/autotitle`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages }),
      });
      const data = r.ok ? await r.json() : null;
      if (data && data.status === 'ok' && data.title) {
        onConversationChange(key, { key, title: data.title });
        setTitleStatus('named');
        setTimeout(() => setTitleStatus('idle'), 1000);
      } else {
        // Server declined (user-titled) or had nothing to work with.
        if (data && data.title_source === 'user') userRenamedRef.current = true;
        setTitleStatus('idle');
      }
    } catch {
      setTitleStatus('idle');
    } finally {
      autoTitleBusyRef.current = false;
    }
  }, [win.key, onConversationChange]);

  // Fired by AutoTitleWatcher on every message change. Names the chat once,
  // after the first complete exchange, then never again (unless the user
  // clears it) — a title that keeps shifting is more distracting than useful.
  const handleAutoTitleMessages = useCallback((messages) => {
    if (userRenamedRef.current || autoTitleBusyRef.current || !win.key) return;
    // Never (re)name a chat that already has a title — opening an EXISTING chat
    // loads its messages, which must not retrigger autotitle. Then name at most
    // ONCE per session (module set survives remounts; the ref-based guard reset
    // on remount and burned tokens).
    if ((win.title && win.title.trim()) || autoTitledKeys.has(win.key) || autoTitleCountRef.current !== 0) return;
    const users = messages.filter((m) => m.role === 'user').length;
    const assistants = messages.filter((m) => m.role === 'assistant').length;
    if (users < 1 || assistants < 1) return;
    autoTitleCountRef.current = 1;
    autoTitledKeys.add(win.key);
    runAutoTitle(messages.slice(-12));
  }, [win.key, win.title, runAutoTitle]);

  return (
    <div
      className={`hub-gateway-panel hub-gw2-panel${rect ? ' is-dragged' : ''}${size ? ' is-resized' : ''}${win.mode === 'min' ? ' is-minimized' : ''}`}
      role="dialog"
      aria-label={win.title || 'Agent Direct Chat'}
      style={{
        zIndex,
        ...(rect
          ? { left: `${rect.left}px`, top: `${rect.top}px`, right: 'auto', bottom: 'auto' }
          // Un-dragged windows stack left-to-right from the content edge
          // (just past the sidebar), matching the minimized dock below.
          : {
            left: `calc(${anchorLeft || '24px'} + ${stackIndex * ((size?.width ?? PANEL_DEFAULT_WIDTH) + PANEL_STACK_GAP)}px)`,
            right: 'auto',
          }),
        ...(size ? { width: `${size.width}px`, height: `${size.height}px` } : null),
      }}
      onPointerDownCapture={() => onFocus(win.key)}
    >
      {/* Bottom-right is the only resize handle; the machinery still keys off
          data-corner, so restoring another corner is just adding it here. */}
      {['br'].map((corner) => (
        <div
          key={corner}
          className={`hub-gw2-resize-handle hub-gw2-resize-handle-${corner}`}
          data-corner={corner}
          onPointerDown={beginPanelResize}
          aria-label="Resize panel from bottom right"
          title="Drag to resize"
        />
      ))}

      <header
        className="hub-gateway-panel-head hub-gw2-head is-draggable"
        onPointerDown={beginPanelDrag}
        title="Drag to move"
      >
        <div className="hub-gw2-head-row hub-gw2-head-row-top">
          <div className="hub-gw2-head-identity">
            <span className="hub-gw2-head-avatar-wrap">
              {agent
                ? <AgentAvatar agent={agent} className="hub-gw2-head-avatar" />
                : <span className="hub-gw2-head-avatar hub-gw2-head-avatar-fallback" aria-hidden="true">M</span>
              }
              <span
                className={`hub-gw2-head-dot is-${statusMeta(state.state).color}`}
                title={`Gateway ${statusMeta(state.state).label}`}
                aria-label={`Gateway ${statusMeta(state.state).label}`}
              />
            </span>
            <strong className="hub-gw2-head-name">{agent?.display_name || 'Agent Direct Chat'}</strong>
          </div>
          <button
            type="button"
            className="hub-gateway-panel-close hub-gw2-head-close hub-gw2-head-min"
            aria-label="Minimize"
            title="Minimize"
            onClick={() => onMinimize(win.key)}
          >−</button>
          <button
            type="button"
            className="hub-gateway-panel-close hub-gw2-head-close"
            aria-label="Close"
            title="Close"
            onClick={() => onClose(win.key)}
          >×</button>
        </div>
        <div className="hub-gw2-head-row hub-gw2-head-row-bottom">
          <div className="hub-gw2-head-conv-stack">
            <div className="hub-gw2-head-conv-row" onPointerDown={(e) => e.stopPropagation()}>
              <svg className="hub-gw2-head-conv-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
              </svg>
              {editingTitle ? (
                <input
                  type="text"
                  className="hub-gw2-head-conv-input"
                  maxLength={30}
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitTitle(); }
                    if (e.key === 'Escape') { e.preventDefault(); setEditingTitle(false); }
                  }}
                  onBlur={commitTitle}
                />
              ) : (
                <button
                  type="button"
                  className="hub-gw2-head-conv is-editable"
                  title={win.key ? 'Rename this chat' : ''}
                  disabled={!win.key}
                  onClick={() => {
                    if (!win.key) return;
                    setTitleDraft(win.title || '');
                    setEditingTitle(true);
                  }}
                >
                  <span className={`hub-gw2-head-conv-text${titleStatus === 'pending' ? ' is-shimmer' : ''}${titleStatus === 'named' ? ' is-revealed' : ''}`}>{win.title || 'Untitled Chat'}</span>
                  <svg className="hub-gw2-head-conv-pen" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          <div className="hub-gw2-head-actions">
            <StartOverButton
              onReset={resetSession}
              isReady={isReady}
            />
          </div>
        </div>
      </header>
      <div className="hub-gw2-body">
        <GatewayThread
          key={windowId(win)}
          sessionKey={win.key}
          agent={agent}
          client={client}
          onResolvedKey={handleResolvedKey}
          onMessages={handleAutoTitleMessages}
        />
      </div>
    </div>
  );
}

export function GatewayPanelV2() {
  const [windows, setWindows] = useState(loadWindows);
  useEffect(() => { saveWindows(windows); }, [windows]);

  const [desktopOpen, setDesktopOpen] = useState(false);
  // Hub-API helper only (creating conversations) — the manager never opens
  // a WebSocket of its own; every ChatWindow owns its own connection.
  const client = useMemo(() => getGatewayClient(), []);

  // Per-window connection states, reported up by each ChatWindow. The
  // launcher pill mirrors the most-recently-focused window's client.
  const [clientStates, setClientStates] = useState({});
  const onClientState = useCallback((id, s) => {
    setClientStates((prev) => (prev[id] === s ? prev : { ...prev, [id]: s }));
  }, []);

  // Operator's agent profile (display name + avatar) for the launcher,
  // window headers, and dock pills.
  const { agent } = useHubAgentState({ preloadAvatar: true });

  const anyWindows = windows.length > 0;
  const openWindows = windows.filter((w) => w.mode === 'open');
  const minimized = windows.filter((w) => w.mode === 'min');

  // Launcher status dot: the most-recently-focused window's connection
  // state ('idle' gray when nothing is open).
  const recentWindow = windows
    .slice()
    .sort((a, b) => (b.lastFocusAt || 0) - (a.lastFocusAt || 0))[0] || null;
  const pill = statusMeta(recentWindow ? (clientStates[windowId(recentWindow)] || 'connecting') : 'idle');

  // The launcher lives in the sidebar footer when the page renders the
  // shared sidebar (app.js leaves a #hub-agent-sidebar-slot there and fires
  // 'hub-sidebar-rendered' after every re-render, since innerHTML replaces
  // the slot node and the portal must re-target the fresh one). Pages with
  // no sidebar (embeds, standalone panes) keep the floating button.
  const [launcherSlot, setLauncherSlot] = useState(() => (
    typeof document !== 'undefined' ? document.getElementById('hub-agent-sidebar-slot') : null
  ));
  useEffect(() => {
    const sync = () => setLauncherSlot(document.getElementById('hub-agent-sidebar-slot'));
    window.addEventListener('hub-sidebar-rendered', sync);
    sync(); // the sidebar may have rendered before this bundle mounted
    return () => window.removeEventListener('hub-sidebar-rendered', sync);
  }, []);

  // Drop connection-state entries for windows that no longer exist.
  useEffect(() => {
    setClientStates((prev) => {
      const ids = new Set(windows.map(windowId));
      const kept = Object.entries(prev).filter(([id]) => ids.has(id));
      return kept.length === Object.keys(prev).length ? prev : Object.fromEntries(kept);
    });
  }, [windows]);

  // The floating desktop is deliberately NOT tied to chat windows — ⌘U
  // opens it standalone and closing the last chat leaves it up. (It used
  // to force-close here whenever no chat windows were open.)

  // The null-key default window resolves its concrete conversation via its
  // own client's connect bootstrap — ChatWindow reports it up through
  // onResolvedKey → onConversationChange(null, {key, title}).

  const focusWindow = useCallback((key) => {
    setWindows((ws) => enforceOpenCap(ws.map((w) => (
      w.key === key ? { ...w, mode: 'open', lastFocusAt: Date.now() } : w
    ))));
  }, []);

  const minimizeWindow = useCallback((key) => {
    setWindows((ws) => ws.map((w) => (w.key === key ? { ...w, mode: 'min' } : w)));
  }, []);

  const closeWindow = useCallback((key) => {
    // Closing a window never deletes the conversation — it stays in the hub
    // registry and can be reopened from the launcher popover.
    setWindows((ws) => ws.filter((w) => w.key !== key));
  }, []);

  const openConversation = useCallback((key, title = '') => {
    setWindows((ws) => {
      if (ws.some((w) => w.key === key)) {
        return enforceOpenCap(ws.map((w) => (
          w.key === key ? { ...w, mode: 'open', lastFocusAt: Date.now() } : w
        )));
      }
      // Hard cap on total windows (== websocket connections): adding a 5th
      // conversation closes the least-recently-focused minimized window,
      // falling back to the least-recently-focused window overall — never
      // an arbitrary array slot, which could be the chat the user is in.
      // Its conversation stays in the hub registry, reopenable any time.
      let next = ws;
      if (ws.length >= MAX_TOTAL_WINDOWS) {
        const byLru = (a, b) => (a.lastFocusAt || 0) - (b.lastFocusAt || 0);
        const lruMin = ws.filter((w) => w.mode === 'min').sort(byLru)[0];
        const evict = lruMin || ws.slice().sort(byLru)[0];
        next = ws.filter((w) => w !== evict);
      }
      return enforceOpenCap([
        ...next,
        { key, title, mode: 'open', rect: null, size: null, lastFocusAt: Date.now() },
      ]);
    });
  }, []);

  const onLayout = useCallback((key, rect, size) => {
    setWindows((ws) => ws.map((w) => (w.key === key ? { ...w, rect, size } : w)));
  }, []);

  const onConversationChange = useCallback((oldKey, conv) => {
    if (!conv || !conv.key) return;
    setWindows((ws) => {
      // Target conversation already open in another window → drop this one
      // and focus that window instead of showing the same session twice.
      if (ws.some((w) => w.key === conv.key && w.key !== oldKey)) {
        return enforceOpenCap(ws
          .filter((w) => w.key !== oldKey)
          .map((w) => (w.key === conv.key ? { ...w, mode: 'open', lastFocusAt: Date.now() } : w)));
      }
      return ws.map((w) => (
        w.key === oldKey ? { ...w, key: conv.key, title: conv.title || '', lastFocusAt: Date.now() } : w
      ));
    });
  }, []);

  // ── Launcher popover: the window/conversation switchboard ──
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const launcherBtnRef = useRef(null);
  const popoverRef = useRef(null);
  const [popoverPos, setPopoverPos] = useState(null);
  // Delete confirmation lives here (declared before the popover's
  // outside-click effect so that effect can keep the list open while the
  // dialog is up). confirmDelete holds the {key,title} awaiting confirmation.
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openPopover = useCallback(() => {
    const btn = launcherBtnRef.current;
    if (btn) {
      const r = btn.getBoundingClientRect();
      setPopoverPos({
        left: Math.max(8, r.left),
        bottom: Math.max(8, window.innerHeight - r.top + 8),
      });
    }
    setPopoverOpen(true);
    fetch('/api/openclaw/chat-sessions?mine=1&limit=12', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => { if (Array.isArray(list)) setConversations(list); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!popoverOpen) return undefined;
    const onDown = (e) => {
      // Keep the chat list open while the delete dialog is up — clicks on
      // the dialog (or its backdrop) must not dismiss the list behind it.
      if (confirmDelete) return;
      if (popoverRef.current?.contains(e.target)) return;
      if (launcherBtnRef.current?.contains(e.target)) return;
      setPopoverOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape' && !confirmDelete) setPopoverOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [popoverOpen, confirmDelete]);

  const onLauncherClick = useCallback(() => {
    if (!anyWindows) {
      // Nothing open yet — skip the popover and open the default
      // conversation directly (its window's client resolves the key).
      openConversation(null, '');
      return;
    }
    if (popoverOpen) setPopoverOpen(false);
    else openPopover();
  }, [anyWindows, popoverOpen, openPopover, openConversation]);

  // New chat is blocked while the floating cap is reached — spawning a 4th
  // window would silently dock one, which reads as a bug. Minimize or close
  // a chat first.
  const newChatBlocked = openWindows.length >= MAX_OPEN_WINDOWS;

  // Chats are auto-named from their first exchange, so "New chat" no longer
  // asks for a name — it opens a window titled "Untitled Chat" immediately.
  // The window is created with an EMPTY title so (a) it displays the
  // "Untitled Chat" fallback and (b) auto-titling is eligible to fire (it
  // skips windows that already carry a title).
  const createUntitledConversation = useCallback(async () => {
    if (newChatBlocked) return;
    try {
      const conv = await client.newConversation({});
      openConversation(conv.key, '');
      setPopoverOpen(false);
    } catch (err) {
      try { window.hubToast?.error?.(`New chat failed: ${err.message || err}`); } catch {}
    }
  }, [client, openConversation, newChatBlocked]);

  // Esc closes the confirm dialog first (capture phase so it wins over the
  // popover's own Esc handler).
  useEffect(() => {
    if (!confirmDelete) return undefined;
    const onEsc = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setConfirmDelete(null); } };
    document.addEventListener('keydown', onEsc, true);
    return () => document.removeEventListener('keydown', onEsc, true);
  }, [confirmDelete]);

  const confirmDeleteChat = useCallback(async () => {
    const target = confirmDelete;
    if (!target || deleting) return;
    setDeleting(true);
    try {
      const r = await fetch(`/api/openclaw/chat-sessions/${encodeURIComponent(target.key)}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // Drop it from the list and close any open window on that key.
      setConversations((cs) => cs.filter((c) => c.key !== target.key));
      setWindows((ws) => ws.filter((w) => w.key !== target.key));
      setConfirmDelete(null);
    } catch (err) {
      try { window.hubToast?.error?.(`Couldn’t delete chat: ${err.message || err}`); } catch {}
    } finally {
      setDeleting(false);
    }
  }, [confirmDelete, deleting]);

  // Windows first (their current titles), then other recent conversations.
  const popoverList = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const w of windows) {
      if (!w.key) continue;
      seen.add(w.key);
      out.push({ key: w.key, title: w.title });
    }
    for (const c of conversations) {
      if (!c || !c.key || seen.has(c.key)) continue;
      seen.add(c.key);
      out.push({ key: c.key, title: c.title });
    }
    return out;
  }, [windows, conversations]);

  // Global ⌘/Ctrl+I toggles the most-recently-focused window (opens the
  // default conversation when none exist); ⌘/Ctrl+U toggles the Desktop —
  // standalone, no chat window required. While this bundle is mounted it
  // owns ⌘U: the flag below tells app.js to skip its open-in-new-tab
  // fallback so one keystroke doesn't do both.
  useEffect(() => {
    window.__hubFabDesktopOwner = true;
    const onKey = (e) => {
      const k = (e.key || '').toLowerCase();
      if (k === 'i' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setWindows((ws) => {
          if (!ws.length) {
            return [{ key: null, title: '', mode: 'open', rect: null, size: null, lastFocusAt: Date.now() }];
          }
          const recent = ws.slice().sort((a, b) => (b.lastFocusAt || 0) - (a.lastFocusAt || 0))[0];
          const mode = recent.mode === 'open' ? 'min' : 'open';
          return enforceOpenCap(ws.map((w) => (
            w === recent ? { ...w, mode, lastFocusAt: Date.now() } : w
          )));
        });
      } else if (k === 'u' && (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) {
        const t = e.target;
        const tag = t && t.tagName ? t.tagName.toLowerCase() : '';
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || (t && t.isContentEditable)) return;
        e.preventDefault();
        setDesktopOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      delete window.__hubFabDesktopOwner;
    };
  }, []);

  // ── Programmatic bridge for the main app ────────────────────────────────
  // Hub pages are plain JS and can't reach into this React tree, so expose a
  // tiny window-level API: open the desktop, open an existing conversation,
  // or spin up a NEW conversation with an auto-sent first prompt (used by the
  // guest drawer's "Enrich/Enhance via LinkedIn" actions). The first prompt
  // rides the pendingPrompts map and is appended by useGatewayBootstrap once
  // the new window's client is ready.
  useEffect(() => {
    window.hubAssistant = {
      openDesktop: () => setDesktopOpen(true),
      openChat: (key = null, title = '') => openConversation(key, title),
      openChatWithPrompt: async ({ prompt = '', title = '', desktop = false } = {}) => {
        const conv = await client.newConversation(title ? { title } : {});
        if (prompt) pendingPrompts.set(conv.key, String(prompt));
        if (desktop) setDesktopOpen(true);
        openConversation(conv.key, title || '');
        return conv.key;
      },
    };
    return () => { delete window.hubAssistant; };
  }, [client, openConversation]);

  // Stacking: focus order → z-index; open windows also get a default
  // horizontal slot from the right edge (until dragged).
  const zRankById = useMemo(() => new Map(
    windows
      .slice()
      .sort((a, b) => (a.lastFocusAt || 0) - (b.lastFocusAt || 0))
      .map((w, i) => [windowId(w), i]),
  ), [windows]);
  const stackIndexById = useMemo(() => new Map(
    openWindows.map((w, i) => [windowId(w), i]),
  ), [openWindows]);

  const dockLeft = launcherSlot ? 'calc(var(--sidebar-width, 260px) + 16px)' : '16px';
  // Un-dragged chat windows share the dock's left alignment.
  const panelAnchorLeft = launcherSlot ? 'calc(var(--sidebar-width, 260px) + 24px)' : '24px';

  const launcherLabel = anyWindows
    ? `${windows.length} chat${windows.length === 1 ? '' : 's'} — click to manage`
    : 'Open assistant (⌘/Ctrl+I)';

  // Every shared-shell page ships a static <nav id="sidebar"> that app.js
  // later fills, growing the launcher slot. On those pages the floating
  // FAB must not flash at bottom-right while the skeleton is up and then
  // teleport into the sidebar — render nothing until the slot arrives.
  // Only pages with no sidebar shell at all keep the floating fallback.
  const expectsSidebarSlot = typeof document !== 'undefined'
    && !!document.getElementById('sidebar');

  return (
    <>
      {desktopOpen && <DesktopPanel floating onClose={() => setDesktopOpen(false)} />}

      {launcherSlot ? createPortal(
        <>
        <button
          type="button"
          className={`sidebar-desktop-btn${desktopOpen ? ' is-open' : ''}`}
          aria-label={desktopOpen ? 'Hide desktop' : 'Show desktop'}
          title={desktopOpen ? 'Hide desktop (⌘/Ctrl+U)' : 'Show desktop (⌘/Ctrl+U)'}
          onClick={() => setDesktopOpen((v) => !v)}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="12" rx="2" />
            <path d="M8 20h8M12 16v4" />
          </svg>
          <span className="sidebar-text sidebar-desktop-btn-label">Show Desktop</span>
          <kbd className="sidebar-desktop-kbd">⌘U</kbd>
        </button>
        <button
          type="button"
          ref={launcherBtnRef}
          className={`sidebar-agent-launcher${openWindows.length ? ' is-open' : ''}`}
          aria-label={launcherLabel}
          title={launcherLabel}
          onClick={onLauncherClick}
        >
          <span className="sidebar-agent-launcher-avatar" aria-hidden="true">
            {agent
              ? <AgentAvatar agent={agent} className="sidebar-agent-launcher-img" />
              : <span className="sidebar-agent-launcher-letter">M</span>
            }
            <span className={`sidebar-agent-launcher-pill is-${pill.color}`} />
          </span>
          <span className="sidebar-text sidebar-agent-launcher-name">
            {agent?.display_name || 'Agent'}
          </span>
          {anyWindows ? (
            <span className="sidebar-agent-launcher-count">{windows.length}</span>
          ) : null}
        </button>
        </>,
        launcherSlot,
      ) : expectsSidebarSlot ? null : (
        <div className="hub-gw2-fab-anchor">
          {!anyWindows && (
            <span className="hub-gw2-fab-hint" aria-hidden="true">⌘I</span>
          )}
          <button
            type="button"
            ref={launcherBtnRef}
            className={`hub-gateway-fab${openWindows.length ? ' is-open' : ''}`}
            aria-label={launcherLabel}
            title={launcherLabel}
            onClick={onLauncherClick}
          >
            <span className="hub-gateway-fab-glyph" aria-hidden="true">
              {agent
                ? <AgentAvatar agent={agent} className="hub-gw2-fab-avatar" />
                : <span className="hub-gw2-fab-letter">M</span>
              }
            </span>
            <span className={`hub-gateway-fab-pill is-${pill.color}`} aria-hidden="true" />
            {anyWindows ? (
              <span className="hub-gateway-fab-count" aria-hidden="true">{windows.length}</span>
            ) : null}
          </button>
        </div>
      )}

      {windows.map((w) => (
        <ChatWindow
          key={windowId(w)}
          win={w}
          agent={agent}
          zIndex={PANEL_BASE_Z + (zRankById.get(windowId(w)) || 0)}
          stackIndex={stackIndexById.get(windowId(w)) || 0}
          anchorLeft={panelAnchorLeft}
          onClientState={onClientState}
          onFocus={focusWindow}
          onMinimize={minimizeWindow}
          onClose={closeWindow}
          onLayout={onLayout}
          onConversationChange={onConversationChange}
        />
      ))}

      {minimized.length > 0 && createPortal(
        <div className="hub-chat-dock" style={{ left: dockLeft }}>
          {minimized.map((w) => (
            <div
              key={windowId(w)}
              className="hub-chat-dock-pill"
              role="button"
              tabIndex={0}
              title={w.title || agent?.display_name || 'Chat'}
              onClick={() => focusWindow(w.key)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); focusWindow(w.key); } }}
            >
              {agent
                ? <AgentAvatar agent={agent} className="hub-chat-dock-avatar" />
                : <span className="hub-chat-dock-avatar hub-chat-dock-avatar-fallback" aria-hidden="true">M</span>
              }
              <span className="hub-chat-dock-title">{w.title || agent?.display_name || 'Chat'}</span>
              <button
                type="button"
                className="hub-chat-dock-close"
                aria-label="Close chat"
                onClick={(e) => { e.stopPropagation(); closeWindow(w.key); }}
              >×</button>
            </div>
          ))}
        </div>,
        document.body,
      )}

      {popoverOpen && popoverPos && createPortal(
        <div
          className="hub-gw2-popover"
          ref={popoverRef}
          style={{ left: `${popoverPos.left}px`, bottom: `${popoverPos.bottom}px` }}
          role="menu"
          aria-label="Chats"
        >
          <button
            type="button"
            className="hub-gw2-popover-row hub-gw2-popover-new"
            onClick={() => { if (!newChatBlocked) createUntitledConversation(); }}
            disabled={newChatBlocked}
            title={newChatBlocked
              ? `Up to ${MAX_OPEN_WINDOWS} chats can float at once — minimize or close one first`
              : 'Start a new conversation'}
          >
            <span className="hub-gw2-popover-plus" aria-hidden="true">+</span> New chat
          </button>
          {popoverList.map((c) => {
            const w = windows.find((x) => x.key === c.key);
            return (
              <div key={c.key} className="hub-gw2-popover-conv">
                <button
                  type="button"
                  className="hub-gw2-popover-open"
                  onClick={() => { openConversation(c.key, c.title || ''); setPopoverOpen(false); }}
                >
                  <span
                    className={`hub-gw2-popover-state${w ? (w.mode === 'open' ? ' is-open' : ' is-min') : ''}`}
                    aria-hidden="true"
                  />
                  <span className="hub-gw2-popover-title">{c.title || c.key}</span>
                </button>
                <button
                  type="button"
                  className="hub-gw2-popover-del"
                  title="Delete chat"
                  aria-label={`Delete chat: ${c.title || c.key}`}
                  onClick={(e) => { e.stopPropagation(); setConfirmDelete({ key: c.key, title: c.title || c.key }); }}
                >
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                    <path d="M10 11v6M14 11v6" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}

      {confirmDelete && createPortal(
        <div
          className="hub-gw2-confirm-overlay"
          onPointerDown={(e) => {
            // Keep the pointerdown from reaching the document-level handler that
            // closes the conversation-list popover — the modal is portaled to
            // <body>, so without this the list hides the moment you touch it.
            e.stopPropagation();
            if (e.target === e.currentTarget) setConfirmDelete(null);
          }}
        >
          <div className="hub-gw2-confirm" role="dialog" aria-modal="true" aria-label="Delete chat">
            <div className="hub-gw2-confirm-title">Delete this chat?</div>
            <div className="hub-gw2-confirm-body">
              <b>{confirmDelete.title}</b> will be permanently deleted. You won’t be able to go back to this chat.
            </div>
            <div className="hub-gw2-confirm-actions">
              <button
                type="button"
                className="hub-gw2-confirm-cancel"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
              >Cancel</button>
              <button
                type="button"
                className="hub-gw2-confirm-delete"
                onClick={confirmDeleteChat}
                disabled={deleting}
                autoFocus
              >{deleting ? 'Deleting…' : 'Delete chat'}</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
